import React, { useState, useEffect, useCallback } from 'react';
import { linksService } from '../services/firestoreService';

// Helper function to extract domain from URL
const getDomain = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch (e) {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
};

// Get favicon URL using Google's favicon service
const getFaviconUrl = (url) => {
  const domain = getDomain(url);
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
};

const LOCAL_STORAGE_KEY = 'webLinks';

// One-time migration: read from localStorage (for backward compatibility)
const loadLinksFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (err) {
    console.error('Error reading links from localStorage:', err);
  }
  return [];
};

const Links = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    description: ''
  });
  const [faviconErrors, setFaviconErrors] = useState(new Set());

  const loadLinks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await linksService.get();
      if (items.length > 0) {
        setLinks(items);
        setLoading(false);
        return;
      }
      const fromStorage = loadLinksFromLocalStorage();
      if (fromStorage.length > 0) {
        await linksService.set(fromStorage);
        setLinks(fromStorage);
        try {
          localStorage.removeItem(LOCAL_STORAGE_KEY);
        } catch (_) {}
      } else {
        setLinks([]);
      }
    } catch (err) {
      console.error('Error loading links:', err);
      setError(err.message || 'Failed to load links');
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({ name: '', url: '', description: '' });
  };

  const handleEdit = (link) => {
    setEditingId(link.id);
    setFormData({
      name: link.name,
      url: link.url,
      description: link.description || ''
    });
    setIsAdding(false);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ name: '', url: '', description: '' });
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.url.trim()) {
      alert('Name and URL are required');
      return;
    }

    // Validate URL format
    let url = formData.url.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    const newLink = {
      id: editingId || Date.now().toString(),
      name: formData.name.trim(),
      url: url,
      description: formData.description.trim()
    };

    let updatedLinks;
    if (editingId) {
      updatedLinks = links.map(link => 
        link.id === editingId ? newLink : link
      );
      setEditingId(null);
    } else {
      updatedLinks = [...links, newLink];
      setIsAdding(false);
    }

    setLinks(updatedLinks);
    setError(null);
    try {
      await linksService.set(updatedLinks);
    } catch (err) {
      console.error('Error saving links:', err);
      setError(err.message || 'Failed to save');
      return;
    }
    setFormData({ name: '', url: '', description: '' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    const updatedLinks = links.filter(link => link.id !== id);
    setLinks(updatedLinks);
    setError(null);
    try {
      await linksService.set(updatedLinks);
    } catch (err) {
      console.error('Error saving links:', err);
      setError(err.message || 'Failed to delete');
      setLinks(links);
    }
  };

  const handleOpenLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleFaviconError = (linkId) => {
    setFaviconErrors(prev => new Set(prev).add(linkId));
  };

  const shouldShowFavicon = (linkId) => {
    return !faviconErrors.has(linkId);
  };

  if (loading) {
    return (
      <div className="vehicle-inner links-page">
        <div className="vehicle-header">
          <h2>Quick Links</h2>
          <p className="vehicle-subtitle">Loading…</p>
        </div>
        <div className="empty-state">Loading links…</div>
      </div>
    );
  }

  return (
    <div className="vehicle-inner links-page">
      {error && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          color: '#b91c1c'
        }}>
          {error}
        </div>
      )}
      <div className="vehicle-header">
        <div>
          <h2>Quick Links</h2>
          <p className="vehicle-subtitle">Manage frequently used web links</p>
        </div>
        {!isAdding && !editingId && (
          <button 
            type="button" 
            className="hero-button hero-primary"
            onClick={handleAdd}
          >
            Add Link
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingId) && (
        <div className="link-form-card" style={{ 
          marginBottom: '24px', 
          padding: '20px', 
          background: 'var(--card-bg)', 
          borderRadius: '12px',
          border: '1px solid var(--border-color)'
        }}>
          <h3 style={{ marginBottom: '16px', fontSize: '18px' }}>
            {editingId ? 'Edit Link' : 'Add New Link'}
          </h3>
          <div className="form-row" style={{ marginBottom: '12px' }}>
            <label htmlFor="linkName">Name *</label>
            <input
              id="linkName"
              name="name"
              type="text"
              placeholder="e.g., DVLA"
              value={formData.name}
              onChange={handleInputChange}
              style={{ width: '100%', padding: '8px 12px' }}
            />
          </div>
          <div className="form-row" style={{ marginBottom: '12px' }}>
            <label htmlFor="linkUrl">URL *</label>
            <input
              id="linkUrl"
              name="url"
              type="text"
              placeholder="https://example.com"
              value={formData.url}
              onChange={handleInputChange}
              style={{ width: '100%', padding: '8px 12px' }}
            />
          </div>
          <div className="form-row" style={{ marginBottom: '16px' }}>
            <label htmlFor="linkDescription">Description</label>
            <input
              id="linkDescription"
              name="description"
              type="text"
              placeholder="Optional description"
              value={formData.description}
              onChange={handleInputChange}
              style={{ width: '100%', padding: '8px 12px' }}
            />
          </div>
          <div className="link-form-actions" style={{ display: 'flex', gap: '12px' }}>
            <button 
              type="button" 
              className="primary-btn"
              onClick={handleSave}
            >
              Save
            </button>
            <button 
              type="button" 
              className="secondary-btn"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Links Grid */}
      <div className="links-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '16px'
      }}>
        {links.length === 0 ? (
          <div className="empty-state">No links added yet. Click "Add Link" to get started.</div>
        ) : (
          links.map(link => (
            <div
              key={link.id}
              className="link-card"
              style={{
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div 
                onClick={() => handleOpenLink(link.url)}
                style={{ flex: 1 }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    background: shouldShowFavicon(link.id) ? 'transparent' : 'var(--primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '18px',
                    overflow: 'hidden',
                    flexShrink: 0
                  }}>
                    {shouldShowFavicon(link.id) ? (
                      <img
                        src={getFaviconUrl(link.url)}
                        alt={`${link.name} favicon`}
                        onError={() => handleFaviconError(link.id)}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      link.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      margin: 0, 
                      fontSize: '16px', 
                      fontWeight: 600,
                      color: 'var(--text-dark)'
                    }}>
                      {link.name}
                    </h3>
                    {link.description && (
                      <p style={{ 
                        margin: '4px 0 0 0', 
                        fontSize: '12px', 
                        color: 'var(--text-muted)'
                      }}>
                        {link.description}
                      </p>
                    )}
                  </div>
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: 'var(--primary)',
                  wordBreak: 'break-all',
                  marginTop: '8px'
                }}>
                  {link.url}
                </div>
              </div>
              <div style={{ 
                display: 'flex', 
                gap: '8px',
                paddingTop: '8px',
                borderTop: '1px solid var(--border-color)'
              }}>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => handleEdit(link)}
                  style={{ flex: 1, fontSize: '12px', padding: '6px 12px' }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => handleDelete(link.id)}
                  style={{ flex: 1, fontSize: '12px', padding: '6px 12px', color: '#cf3c5a' }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Links;

